import { useSyncExternalStore } from "react";
import type {
  Badge,
  FishFinderState,
  GeoLocation,
  LifelistEntry,
  Species,
  Trip,
  VisitedLocation,
} from "./types";
import { syncBadges } from "./badges";
import { placeNames, logToSharedTrip, lookupSpecies } from "./api";
import { normName, hasRealCommonName } from "../../shared/species";
import { logEvent } from "./events";
import { toast } from "../components/Toaster";

// iNat taxon ids are always < this; anything at/above is a synthetic id
// minted by the GBIF/OBIS merge for a species iNat didn't return. Kept
// in sync with SYNTHETIC_MIN in src/worker/factsheet.ts — if that
// constant moves, move this too.
const SYNTHETIC_TAXON_MIN = 1_000_000_000;

const STORAGE_KEY = "fish-finder:v1";
// perf-options F8: extended from 24h to 7d. The underlying /api/species
// data changes on a weekly-plus cadence in practice (iNat counts drift
// slowly, marine taxonomy near-static), and any sort / filter change on
// the server ships with a new `sort_version` that invalidates cached
// rows on read regardless of TTL. So a longer freshness window costs
// nothing when we ship real changes, and saves the /api/species round
// trip for every repeat visit to the same reef in a given week.
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Bump this version whenever the server-side species sort / filter logic
// changes in a way that would otherwise leave users staring at up to a day
// of pre-fix data from their visited_locations cache. On first read after
// a bump, every cached_at is rewound to the epoch (so the next visit
// refetches), then the new version is stamped so the bust only fires once.
// Using a version flag instead of a wall-clock cutoff avoids the trap of
// picking a timestamp that's already behind the user's most recent cache.
const VISIT_CACHE_VERSION = 8;
const VISIT_CACHE_VERSION_KEY = "fish-finder:visit-cache-version";

const EMPTY: FishFinderState = {
  lifelist: [],
  visited_locations: [],
  badges: [],
  trips: [],
  active_trip_id: null,
};

// One-shot visited-cache bust. Compares the stored bust version against the
// current code's VISIT_CACHE_VERSION; on a bump, every visited_locations
// entry is force-staled (cached_at rewound to epoch) so the very next visit
// hits the server and picks up the new sort/filter rules. Then the new
// version is written so the bust never re-fires.
function bustStaleVisitsIfNeeded(
  visited: VisitedLocation[],
): VisitedLocation[] {
  try {
    const stored = Number(localStorage.getItem(VISIT_CACHE_VERSION_KEY) ?? "0");
    if (stored >= VISIT_CACHE_VERSION) return visited;
    localStorage.setItem(VISIT_CACHE_VERSION_KEY, String(VISIT_CACHE_VERSION));
    return visited.map((v) => ({
      ...v,
      cached_at: new Date(0).toISOString(),
    }));
  } catch {
    return visited;
  }
}

function read(): FishFinderState {
  if (typeof localStorage === "undefined") return EMPTY;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // Mark the bust as done on a fresh device so it doesn't fire later if
      // the user signs in and pulls a (potentially stale) durable copy.
      try {
        localStorage.setItem(
          VISIT_CACHE_VERSION_KEY,
          String(VISIT_CACHE_VERSION),
        );
      } catch {
        /* ignore */
      }
      return EMPTY;
    }
    const parsed = JSON.parse(raw) as Partial<FishFinderState>;
    const rawLife = Array.isArray(parsed.lifelist) ? parsed.lifelist : [];
    // Self-heal: existing installs may carry duplicate visited-location
    // entries from the days when only coordinate keys deduped. Collapse
    // them on load so the Recent spots list opens clean on the very next
    // launch after this ships.
    const visited_locations = dedupeVisitedLocations(
      bustStaleVisitsIfNeeded(
        Array.isArray(parsed.visited_locations) ? parsed.visited_locations : [],
      ),
    );
    return {
      // Migrate legacy single trip_id -> trip_ids[]; ensure the array exists.
      lifelist: rawLife.map((e) => {
        const legacy = (e as { trip_id?: string | null }).trip_id;
        const ids = Array.isArray(e.trip_ids)
          ? e.trip_ids
          : typeof legacy === "string"
            ? [legacy]
            : [];
        return {
          ...e,
          trip_ids: [...new Set(ids)],
          last_seen: e.last_seen || e.first_seen,
        };
      }),
      visited_locations,
      badges: Array.isArray(parsed.badges) ? parsed.badges : [],
      trips: Array.isArray(parsed.trips) ? parsed.trips : [],
      active_trip_id: parsed.active_trip_id ?? null,
    };
  } catch {
    return EMPTY;
  }
}

let state: FishFinderState = read();
const listeners = new Set<() => void>();

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — keep in-memory state */
  }
}

const DIRTY_KEY = "fish-finder:dirty";

function readDirty(): boolean {
  try {
    return localStorage.getItem(DIRTY_KEY) === "1";
  } catch {
    return false;
  }
}
let dirty = readDirty();

function setDirty(v: boolean) {
  dirty = v;
  try {
    if (v) localStorage.setItem(DIRTY_KEY, "1");
    else localStorage.removeItem(DIRTY_KEY);
  } catch {
    /* ignore */
  }
}

function commit(next: FishFinderState, markDirty: boolean) {
  state = next;
  persist();
  if (markDirty) {
    setDirty(true);
    scheduleSync();
  }
  listeners.forEach((l) => l());
}

// Local mutations go through here and are flagged for durable sync.
function setState(next: FishFinderState) {
  commit(next, true);
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getState(): FishFinderState {
  return state;
}

// The store snapshot must be referentially stable between renders or
// useSyncExternalStore re-renders forever. So we snapshot the whole `state`
// (its reference only changes on setState) and derive with the selector
// during render — never feed a fresh object into getSnapshot.
export function useFishStore<T>(selector: (s: FishFinderState) => T): T {
  const snapshot = useSyncExternalStore(subscribe, getState, getState);
  return selector(snapshot);
}

// ---- helpers ----

export function locationKey(lat: number, lng: number): string {
  return `${lat.toFixed(3)},${lng.toFixed(3)}`;
}

// Normalized display-name key used to consolidate visited-location entries
// that carry the same human name but slightly different coordinates. That
// happens routinely: one search picks the beach from the autocomplete
// dropdown (name = "Kahaluʻu, Kahaluu-Keauhou", lat/lng from Nominatim),
// another taps "Use my location" nearby (same reef, GPS coords a few
// hundred meters off) — coordinate-only dedup lets both entries survive,
// which reads as a UI bug ("why is Recent spots showing the same reef
// twice?"). Match on name + a coarse coordinate bucket so we only merge
// when they're genuinely the same place, not two beaches that happen to
// share a name in different regions.
const NAME_KEY_COORD_PRECISION = 1; // ~11 km — "same reef", not "same country"
export function nameKey(name: string, lat: number, lng: number): string {
  const n = name.trim().toLowerCase().replace(/\s+/g, " ");
  return `${n}|${lat.toFixed(NAME_KEY_COORD_PRECISION)},${lng.toFixed(NAME_KEY_COORD_PRECISION)}`;
}

// Collapse any visited_locations entries that share (normalized name + coarse
// coordinate bucket). Keeps the FIRST occurrence — since the list is stored
// most-recent first, that means we keep the freshest cached_species and
// last_visited while dropping the stale twin. Idempotent.
function dedupeVisitedLocations(
  visited: VisitedLocation[],
): VisitedLocation[] {
  const seen = new Set<string>();
  const out: VisitedLocation[] = [];
  for (const v of visited) {
    const nk = nameKey(v.name, v.lat, v.lng);
    if (seen.has(nk)) continue;
    seen.add(nk);
    out.push(v);
  }
  return out;
}

export function isInLifelist(taxonId: number): boolean {
  return state.lifelist.some((e) => e.taxon_id === taxonId);
}

// Resolve a species to its lifelist entry. Matches on taxon_id first, then
// falls back to the stable scientific-name key — a species' taxon_id flips
// between a real iNaturalist id and a synthetic hash depending on which
// source returned it, so the same fish can carry different ids across
// visits and taxon_id alone can't be the identity key.
export function lifelistEntryFor(species: {
  taxon_id: number;
  scientific_name: string;
}): LifelistEntry | undefined {
  const byId = state.lifelist.find((e) => e.taxon_id === species.taxon_id);
  if (byId) return byId;
  const key = normName(species.scientific_name);
  if (!key) return undefined;
  return state.lifelist.find((e) => normName(e.scientific_name) === key);
}

// ---- actions ----

// Server's current sort_version (see src/worker/index.ts and shared/types.
// ts::SpeciesResponse). Cached rows stamped with anything other than this
// value are treated as stale on read so a sort-order rev takes effect the
// next time the app opens — no manual VISIT_CACHE_VERSION bump required.
const EXPECTED_SORT_VERSION = "backfill-wikipedia-fallback-v1";

export function recordVisit(
  location: GeoLocation,
  radiusKm: number,
  species: Species[],
  sortVersion?: string,
): void {
  const key = locationKey(location.lat, location.lng);
  const nk = nameKey(location.name, location.lat, location.lng);
  const now = new Date().toISOString();
  const entry: VisitedLocation = {
    key,
    name: location.name,
    lat: location.lat,
    lng: location.lng,
    radius_km: radiusKm,
    // Legacy field kept on the type/schema for back-compat with rows that
    // already have it set; the UI no longer exposes this filter.
    research_only: false,
    last_visited: now,
    cached_species: species,
    cached_at: now,
    sort_version: sortVersion,
  };
  // Drop any prior entries that match the new one either by exact coord key
  // (a re-search of the same spot) OR by name-key (same reef with slightly
  // different coords — e.g. autocomplete pick vs. "Use my location") so a
  // single physical reef never surfaces twice in Recent spots.
  const visited_locations = [
    entry,
    ...state.visited_locations.filter(
      (v) => v.key !== key && nameKey(v.name, v.lat, v.lng) !== nk,
    ),
  ];
  const nextBase = { ...state, visited_locations };
  const { badges } = syncBadges(nextBase);
  setState({ ...nextBase, badges });
}

// Remember a place's coordinates without clobbering a richer existing entry.
// Used when a spot is attached to a trip from search (option B): so a
// non-curated beach like "Pauoa Bay" can be resolved to coordinates later
// (on the map, the Plan page, the shared trip) even if you never logged a
// fish there. If a visited location for the same reef already exists — with
// its real cached species — it's left untouched.
export function rememberPlace(name: string, lat: number, lng: number): void {
  const clean = name.trim().replace(/\s+/g, " ");
  if (!clean || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
  const key = locationKey(lat, lng);
  const nk = nameKey(clean, lat, lng);
  const known = state.visited_locations.some(
    (v) => v.key === key || nameKey(v.name, v.lat, v.lng) === nk,
  );
  if (known) return;
  const now = new Date().toISOString();
  const entry: VisitedLocation = {
    key,
    name: clean,
    lat,
    lng,
    radius_km: 10,
    research_only: false,
    last_visited: now,
    cached_species: [],
    cached_at: now,
  };
  setState({
    ...state,
    visited_locations: [entry, ...state.visited_locations],
  });
}

// Drop a single visited-location entry by its coord key. Called from the
// long-press remove flow on the Home page. Does not touch the lifelist —
// species you logged from that spot stay in your collection, only the
// "recent spot" shortcut goes away.
export function removeVisit(key: string): void {
  const visited_locations = state.visited_locations.filter((v) => v.key !== key);
  if (visited_locations.length === state.visited_locations.length) return;
  const nextBase = { ...state, visited_locations };
  const { badges } = syncBadges(nextBase);
  setState({ ...nextBase, badges });
}

export function getCachedVisit(
  lat: number,
  lng: number,
  radiusKm: number,
): VisitedLocation | null {
  const key = locationKey(lat, lng);
  const loc = state.visited_locations.find((v) => v.key === key);
  if (!loc) return null;
  if (loc.radius_km !== radiusKm) return null;
  if (Date.now() - new Date(loc.cached_at).getTime() > CACHE_TTL_MS) {
    return null;
  }
  // Sort-order rev fell out from under this cached row (or the row predates
  // the sort_version tag entirely) — discard so the next search re-fetches
  // and picks up the current order.
  if (loc.sort_version !== EXPECTED_SORT_VERSION) return null;
  return loc;
}

export interface AddResult {
  outcome: "added" | "trip-added" | "noop";
  tripName: string | null; // active trip name, if a trip was involved
  unlocked: Badge[];
}

// Mark a species seen. New → add it (joining the active trip if any).
// Already seen → accumulate the active trip onto it. Never removes (that's
// removeFromLifelist) so "which trips I've seen it in" can build up.
export function toggleLifelist(
  species: Species,
  locationName: string,
  photoUrl?: string,
): AddResult {
  const active = state.active_trip_id ?? null;
  const tripName = active
    ? (state.trips.find((t) => t.id === active)?.name ?? null)
    : null;
  const now = new Date().toISOString();
  // Match an existing entry by taxon_id or by the stable scientific-name
  // key, so a species whose id flipped between sources doesn't get logged
  // twice as two separate lifelist entries.
  const nameKey = normName(species.scientific_name);
  const idx = state.lifelist.findIndex(
    (e) =>
      e.taxon_id === species.taxon_id ||
      (nameKey !== "" && normName(e.scientific_name) === nameKey),
  );

  let lifelist: LifelistEntry[];
  let outcome: AddResult["outcome"];

  if (idx === -1) {
    const entry: LifelistEntry = {
      taxon_id: species.taxon_id,
      common_name: species.common_name,
      scientific_name: species.scientific_name,
      observation_count: species.observation_count,
      first_seen: now,
      last_seen: now,
      location_name: locationName,
      photo_url: photoUrl || species.photo_url,
      trip_ids: active ? [active] : [],
      category: species.category,
    };
    lifelist = [entry, ...state.lifelist];
    outcome = "added";
  } else {
    // Any re-tap is "I saw it again": bump last_seen so it rises in All.
    const cur = state.lifelist[idx];
    const addsTrip = !!active && !cur.trip_ids.includes(active);
    const updated: LifelistEntry = {
      ...cur,
      last_seen: now,
      // Fill an empty photo with the user's own shot; don't overwrite one.
      photo_url: cur.photo_url || photoUrl || "",
      trip_ids: addsTrip ? [...cur.trip_ids, active!] : cur.trip_ids,
    };
    lifelist = state.lifelist.map((e, i) => (i === idx ? updated : e));
    outcome = addsTrip ? "trip-added" : "noop";
  }

  const nextBase = { ...state, lifelist };
  const { badges, unlocked } = syncBadges(nextBase);
  setState({ ...nextBase, badges });
  // Best-effort fanout to co-buddies on a shared trip. Fire-and-forget so
  // the local UI never waits on network; the /api/state sync still moves
  // the personal lifelist even if the shared log call fails.
  if (active && (outcome === "added" || outcome === "trip-added")) {
    void logToSharedTrip(active, {
      taxon_id: species.taxon_id,
      scientific_name: species.scientific_name,
      common_name: species.common_name,
      photo_url: photoUrl || species.photo_url,
      location_name: locationName,
    }).catch(() => void 0);
  }
  // Diagnostic breadcrumb. Captures the "shape" of the entry we saved
  // so a later "why does this card look wrong?" question has an answer.
  logEvent("fish.add", {
    outcome,
    taxon_id: species.taxon_id,
    is_synthetic_taxon: species.taxon_id >= SYNTHETIC_TAXON_MIN,
    scientific_name: species.scientific_name,
    common_name: species.common_name,
    has_real_common_name: hasRealCommonName(
      species.common_name,
      species.scientific_name,
    ),
    has_photo: !!(photoUrl || species.photo_url),
    photo_from_user_capture: !!photoUrl,
    category: species.category || "",
    location_name: locationName,
    active_trip_id: active,
    lifelist_size_after: lifelist.length,
    badges_unlocked: unlocked.length,
  });
  return { outcome, tripName, unlocked };
}

// Replace one lifelist entry's photo with a different URL — powers the
// "wrong photo?" manual swap. No-op if the entry doesn't exist or the
// url is the same, so callers can fire without pre-checking. Fires a
// photo.swap event so we can look at which taxa keep getting swapped
// and turn them into an auto-rule later.
export function swapLifelistPhoto(taxonId: number, newUrl: string): void {
  const idx = state.lifelist.findIndex((e) => e.taxon_id === taxonId);
  if (idx === -1) return;
  const cur = state.lifelist[idx];
  if (cur.photo_url === newUrl) return;
  const updated: LifelistEntry = { ...cur, photo_url: newUrl };
  setState({
    ...state,
    lifelist: state.lifelist.map((e, i) => (i === idx ? updated : e)),
  });
  logEvent("photo.swap", {
    taxon_id: taxonId,
    scientific_name: cur.scientific_name,
    common_name: cur.common_name,
    from: cur.photo_url || "",
    to: newUrl,
    source: "wikipedia",
  });
}

// Broader "refresh" — takes a full Species (typically a resolved
// lookupSpecies result) and updates every field on the lifelist entry
// that was stale. Photo, common name, category. Preserves the entry's
// history (first_seen, last_seen, trip_ids, location_name) and returns
// which fields actually changed so the caller can toast intelligently.
export interface LifelistRefreshResult {
  photo_changed: boolean;
  common_name_changed: boolean;
  category_changed: boolean;
}
export function refreshLifelistFromSpecies(
  taxonId: number,
  fresh: Species,
): LifelistRefreshResult {
  const idx = state.lifelist.findIndex((e) => e.taxon_id === taxonId);
  const noChange = {
    photo_changed: false,
    common_name_changed: false,
    category_changed: false,
  };
  if (idx === -1) return noChange;
  const cur = state.lifelist[idx];

  // Only overwrite fields where the fresh answer is BETTER (has real
  // data vs. the current fallback / empty). Don't clobber a good field
  // with an empty one. Common name is treated a bit more permissively:
  // if the fresh version is a real name AND differs from what's
  // stored, take it — that lets a subsequent refresh replace a
  // sentence-cased Wikipedia name with iNat's title-cased one (or
  // vice-versa, once we title-case Wikipedia at source).
  const takePhoto = !!fresh.photo_url && fresh.photo_url !== cur.photo_url;
  const takeName =
    hasRealCommonName(fresh.common_name, fresh.scientific_name) &&
    fresh.common_name !== cur.common_name;
  const freshCat = fresh.category ?? "";
  const takeCategory =
    !!freshCat &&
    freshCat !== "Other" &&
    (!cur.category || cur.category === "Other") &&
    freshCat !== cur.category;
  if (!takePhoto && !takeName && !takeCategory) return noChange;

  const updated: LifelistEntry = {
    ...cur,
    photo_url: takePhoto ? fresh.photo_url : cur.photo_url,
    common_name: takeName ? fresh.common_name : cur.common_name,
    category: takeCategory ? freshCat : cur.category,
  };
  setState({
    ...state,
    lifelist: state.lifelist.map((e, i) => (i === idx ? updated : e)),
  });
  logEvent("lifelist.refresh", {
    taxon_id: taxonId,
    scientific_name: cur.scientific_name,
    photo_changed: takePhoto,
    common_name_changed: takeName,
    category_changed: takeCategory,
    new_common_name: takeName ? fresh.common_name : "",
    new_category: takeCategory ? freshCat : "",
  });
  return {
    photo_changed: takePhoto,
    common_name_changed: takeName,
    category_changed: takeCategory,
  };
}

// Fire-and-forget delete of the R2-hosted photo when a lifelist entry
// (or trip attach) drops it. Legacy public photos, off-list resolves
// with a Wikipedia/iNat URL, and anon-mode lifelist entries all skip
// harmlessly because the endpoint 204s on unknown / unowned keys.
export function scheduleDeletePhotoUrl(photoUrl: string): void {
  if (!photoUrl.startsWith("/api/photo/")) return;
  const key = photoUrl.slice("/api/photo/".length);
  if (!key) return;
  fetch(`/api/photo/${key}`, { method: "DELETE" }).catch(() => {
    /* best-effort; a scheduled sweep can retry */
  });
}

export function removeFromLifelist(taxonId: number): void {
  const before = state.lifelist.find((e) => e.taxon_id === taxonId);
  const lifelist = state.lifelist.filter((e) => e.taxon_id !== taxonId);
  const nextBase = { ...state, lifelist };
  const { badges } = syncBadges(nextBase);
  setState({ ...nextBase, badges });
  if (before) {
    logEvent("fish.remove", {
      taxon_id: taxonId,
      scientific_name: before.scientific_name,
      common_name: before.common_name,
      source: "lifelist",
      lifelist_size_after: lifelist.length,
    });
    scheduleDeletePhotoUrl(before.photo_url ?? "");
  }
}

export type TripRemoval = "detached" | "removed" | "noop";

// Spot-tab "remove from this trip": detach one trip from a species. If that
// was its only trip the species wasn't seen anywhere else, so drop it from
// the lifelist entirely; otherwise it stays (still on its other trips).
export function removeSpeciesFromTrip(
  taxonId: number,
  tripId: string,
): TripRemoval {
  const idx = state.lifelist.findIndex((e) => e.taxon_id === taxonId);
  if (idx === -1) return "noop";
  const cur = state.lifelist[idx];
  if (!cur.trip_ids.includes(tripId)) return "noop";

  const remaining = cur.trip_ids.filter((t) => t !== tripId);
  if (remaining.length === 0) {
    const lifelist = state.lifelist.filter((e) => e.taxon_id !== taxonId);
    const nextBase = { ...state, lifelist };
    const { badges } = syncBadges(nextBase);
    setState({ ...nextBase, badges });
    logEvent("fish.remove", {
      taxon_id: taxonId,
      scientific_name: cur.scientific_name,
      common_name: cur.common_name,
      source: "trip",
      trip_id: tripId,
      lifelist_size_after: lifelist.length,
    });
    scheduleDeletePhotoUrl(cur.photo_url ?? "");
    return "removed";
  }
  const updated: LifelistEntry = { ...cur, trip_ids: remaining };
  setState({
    ...state,
    lifelist: state.lifelist.map((e, i) => (i === idx ? updated : e)),
  });
  logEvent("trip.detach_species", {
    taxon_id: taxonId,
    scientific_name: cur.scientific_name,
    trip_id: tripId,
    remaining_trips: remaining.length,
  });
  return "detached";
}

// One-time category backfill: apply server-resolved categories to entries
// whose category is missing or "Other". Returns how many were corrected.
export function applyCategories(map: Record<string, string>): number {
  let changed = 0;
  const lifelist = state.lifelist.map((e) => {
    const next = map[String(e.taxon_id)];
    if (next && next !== (e.category ?? "") && (!e.category || e.category === "Other")) {
      changed += 1;
      return { ...e, category: next };
    }
    return e;
  });
  if (changed > 0) setState({ ...state, lifelist });
  return changed;
}

export function clearAll(): void {
  setState({ ...EMPTY });
}

// A visited spot whose name is the literal "My location" (older builds) or a
// bare "lat, lng" string (a past reverse-geocode miss) — i.e. not a real
// place name. The coord pattern matches the server's fallback formatting.
const COORD_RE = /^\s*-?\d{1,3}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?\s*$/;

function needsPlaceName(name: string): boolean {
  const n = name.trim();
  if (!n) return true;
  if (n.toLowerCase() === "my location") return true;
  return COORD_RE.test(n);
}

let placeNamesDone = false;

// One-time auto cleanup (called once after hydrate): resolve legacy
// "My location" / raw-coordinate visited spots to real place names and
// cascade the rename into the lifelist + badges so the collection stops
// grouping anything under "My location". Only an unambiguous 1:1 old→new
// mapping is cascaded onto coord-less lifelist/badge rows, so two distinct
// "My location" spots are never merged.
export async function backfillPlaceNames(): Promise<number> {
  if (placeNamesDone || typeof fetch !== "function") return 0;
  placeNamesDone = true;

  const stale = state.visited_locations.filter(
    (v) =>
      Number.isFinite(v.lat) && Number.isFinite(v.lng) && needsPlaceName(v.name),
  );
  if (stale.length === 0) return 0;

  let resolved: Record<string, string>;
  try {
    const r = await placeNames({
      points: stale.map((v) => ({ key: v.key, lat: v.lat, lng: v.lng })),
    });
    resolved = r.names;
  } catch {
    placeNamesDone = false; // transient — let a later app load retry
    return 0;
  }

  const oldToNew = new Map<string, Set<string>>();
  for (const v of stale) {
    const nn = resolved[v.key];
    if (!nn) continue;
    const set = oldToNew.get(v.name) ?? new Set<string>();
    set.add(nn);
    oldToNew.set(v.name, set);
  }
  const renameByName = (old: string): string | null => {
    const set = oldToNew.get(old);
    return set && set.size === 1 ? [...set][0] : null;
  };

  let changed = false;
  const visited_locations = state.visited_locations.map((v) => {
    const nn = resolved[v.key];
    if (nn && needsPlaceName(v.name) && nn !== v.name) {
      changed = true;
      return { ...v, name: nn };
    }
    return v;
  });
  const lifelist = state.lifelist.map((e) => {
    const nn = renameByName(e.location_name);
    if (nn && nn !== e.location_name) {
      changed = true;
      return { ...e, location_name: nn };
    }
    return e;
  });
  const badges = state.badges.map((b) => {
    const nn = renameByName(b.location_name);
    if (nn && nn !== b.location_name) {
      changed = true;
      return { ...b, location_name: nn };
    }
    return b;
  });

  if (changed) setState({ ...state, visited_locations, lifelist, badges });
  return stale.length;
}

// One-time-per-session backfill of common names AND missing photos for
// lifelist entries that lack them — species logged off-list (stranded on
// their scientific name) or ones with a name but no photo (e.g. a dolphin
// or shark you couldn't photograph, which should still show a stock species
// image). Resolves each via the same iNat/Wikipedia lookup the identify flow
// uses, then updates in place (which marks dirty and syncs). Bounded per
// run; the rest are picked up on later loads. See
// lifelist-scientific-only-common-name-backfill.
let commonNamesDone = false;
export async function backfillCommonNames(): Promise<number> {
  if (commonNamesDone || typeof fetch !== "function") return 0;
  commonNamesDone = true;

  const stale = state.lifelist.filter(
    (e) =>
      e.scientific_name.trim() !== "" &&
      (!hasRealCommonName(e.common_name, e.scientific_name) || !e.photo_url),
  );
  if (stale.length === 0) return 0;

  const MAX = 25;
  const CONCURRENCY = 4;
  const batch = stale.slice(0, MAX);
  const resolved = new Map<string, { common_name: string; photo_url: string }>();
  let idx = 0;
  const worker = async () => {
    while (idx < batch.length) {
      const e = batch[idx++];
      try {
        const sp = await lookupSpecies(e.scientific_name);
        if (
          hasRealCommonName(sp.common_name, sp.scientific_name) ||
          sp.photo_url
        ) {
          resolved.set(normName(e.scientific_name), {
            common_name: sp.common_name,
            photo_url: sp.photo_url,
          });
        }
      } catch {
        /* skip this one; a later load retries the still-stale entries */
      }
    }
  };
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));

  if (resolved.size === 0) {
    commonNamesDone = false; // nothing landed (maybe transient) — retry later
    return 0;
  }

  let changed = false;
  const lifelist = state.lifelist.map((e) => {
    const r = resolved.get(normName(e.scientific_name));
    if (!r) return e;
    let next = e;
    if (
      r.common_name &&
      hasRealCommonName(r.common_name, e.scientific_name) &&
      r.common_name !== e.common_name
    ) {
      next = { ...next, common_name: r.common_name };
      changed = true;
    }
    if (!e.photo_url && r.photo_url) {
      next = { ...next, photo_url: r.photo_url };
      changed = true;
    }
    return next;
  });
  if (changed) setState({ ...state, lifelist });
  return resolved.size;
}

// ---- trips ----

function newId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// Create a trip and make it the active one (new sightings auto-join it).
export function createTrip(name: string): Trip {
  const trip: Trip = {
    id: newId(),
    name: name.trim() || "Untitled trip",
    created_at: new Date().toISOString(),
  };
  setState({
    ...state,
    trips: [trip, ...state.trips],
    active_trip_id: trip.id,
  });
  logEvent("trip.create", {
    trip_id: trip.id,
    name: trip.name,
    trips_after: state.trips.length + 1,
  });
  return trip;
}

// Add a named beach to a trip. Case-insensitively de-dups against what's
// already on the trip (typing "two step" after "Two Step" is a no-op).
// Prepends so the trip's beach list stays "most recent first" without any
// extra timestamp bookkeeping.
export function addBeachToTrip(id: string, name: string): void {
  const clean = name.trim().replace(/\s+/g, " ");
  if (!clean) return;
  const key = clean.toLowerCase();
  const trip = state.trips.find((t) => t.id === id);
  const wasExisting = !!trip?.beaches?.some(
    (b) => b.trim().toLowerCase() === key,
  );
  setState({
    ...state,
    trips: state.trips.map((t) => {
      if (t.id !== id) return t;
      const existing = t.beaches ?? [];
      if (existing.some((b) => b.trim().toLowerCase() === key)) return t;
      return { ...t, beaches: [clean, ...existing] };
    }),
  });
  logEvent("beach.add", {
    trip_id: id,
    name: clean,
    outcome: wasExisting ? "noop-duplicate" : "added",
    beach_count_after: (trip?.beaches?.length ?? 0) + (wasExisting ? 0 : 1),
  });
}

export function removeBeachFromTrip(id: string, name: string): void {
  const key = name.trim().replace(/\s+/g, " ").toLowerCase();
  if (!key) return;
  setState({
    ...state,
    trips: state.trips.map((t) => {
      if (t.id !== id) return t;
      const existing = t.beaches ?? [];
      const next = existing.filter((b) => b.trim().toLowerCase() !== key);
      if (next.length === existing.length) return t;
      return { ...t, beaches: next };
    }),
  });
  logEvent("beach.remove", { trip_id: id, name });
}

export function renameTrip(id: string, name: string): void {
  const cur = state.trips.find((t) => t.id === id);
  setState({
    ...state,
    trips: state.trips.map((t) =>
      t.id === id ? { ...t, name: name.trim() || t.name } : t,
    ),
  });
  logEvent("trip.rename", {
    trip_id: id,
    from: cur?.name ?? "",
    to: name.trim(),
  });
}

// Deleting a trip keeps the sightings — it just untags them.
export function deleteTrip(id: string): void {
  const trip = state.trips.find((t) => t.id === id);
  const affected = state.lifelist.filter((e) => e.trip_ids.includes(id)).length;
  setState({
    ...state,
    trips: state.trips.filter((t) => t.id !== id),
    lifelist: state.lifelist.map((e) =>
      e.trip_ids.includes(id)
        ? { ...e, trip_ids: e.trip_ids.filter((x) => x !== id) }
        : e,
    ),
    active_trip_id: state.active_trip_id === id ? null : state.active_trip_id,
  });
  logEvent("trip.delete", {
    trip_id: id,
    name: trip?.name ?? "",
    species_detached: affected,
  });
}

export function setActiveTrip(id: string | null): void {
  const prev = state.active_trip_id ?? null;
  setState({ ...state, active_trip_id: id });
  if (prev !== id) {
    const name = id ? state.trips.find((t) => t.id === id)?.name ?? "" : "";
    logEvent("trip.set_active", { from: prev, to: id, name });
  }
}

// ---- bulk import (reconstruct old trips from photos) ----

export interface ImportSighting {
  species: Species;
  photoUrl: string;
  whenISO: string; // photo's EXIF date (or file mtime)
}

export interface ImportTripInput {
  name: string;
  createdAtISO: string; // the trip's date (earliest photo)
  locationName: string;
  sightings: ImportSighting[];
}

export interface BulkImportResult {
  trips: number;
  entries: number; // lifelist entries created or updated
  unlocked: Badge[];
}

// Atomically create one trip per group and fold each sighting into the
// lifelist (merging by taxon_id: union trip_ids, widen first/last seen,
// fill an empty photo). Does NOT change the active trip — this is history,
// not the current outing. One setState ⇒ one durable sync.
export function bulkImportTrips(
  groups: ImportTripInput[],
): BulkImportResult {
  const trips: Trip[] = [];
  const byTaxon = new Map<number, LifelistEntry>();
  for (const e of state.lifelist) byTaxon.set(e.taxon_id, { ...e });

  let entriesTouched = 0;

  for (const g of groups) {
    if (g.sightings.length === 0) continue;
    const trip: Trip = {
      id: newId(),
      name: g.name.trim() || "Imported trip",
      created_at: g.createdAtISO,
    };
    trips.push(trip);

    for (const s of g.sightings) {
      const cur = byTaxon.get(s.species.taxon_id);
      if (!cur) {
        byTaxon.set(s.species.taxon_id, {
          taxon_id: s.species.taxon_id,
          common_name: s.species.common_name,
          scientific_name: s.species.scientific_name,
          observation_count: s.species.observation_count,
          first_seen: s.whenISO,
          last_seen: s.whenISO,
          location_name: g.locationName,
          photo_url: s.photoUrl || s.species.photo_url,
          trip_ids: [trip.id],
          category: s.species.category,
        });
      } else {
        const earlier =
          s.whenISO < cur.first_seen ? s.whenISO : cur.first_seen;
        const laterBase = cur.last_seen || cur.first_seen;
        cur.first_seen = earlier;
        cur.last_seen = s.whenISO > laterBase ? s.whenISO : laterBase;
        if (!cur.trip_ids.includes(trip.id)) cur.trip_ids.push(trip.id);
        if (!cur.photo_url) cur.photo_url = s.photoUrl || "";
      }
      entriesTouched++;
    }
  }

  if (trips.length === 0) {
    return { trips: 0, entries: 0, unlocked: [] };
  }

  const nextBase: FishFinderState = {
    ...state,
    trips: [...trips, ...state.trips],
    lifelist: [...byTaxon.values()],
  };
  const { badges, unlocked } = syncBadges(nextBase);
  setState({ ...nextBase, badges });
  return { trips: trips.length, entries: entriesTouched, unlocked };
}

// ---- durable sync (Cloudflare KV, single user, no auth) ----
//
// localStorage stays the instant/offline cache. The server copy is the
// cross-device source of truth. Strategy: last-write-wins, but a device only
// pushes when it has local changes (dirty flag), so deletions/clears
// propagate. On first run with an empty server but existing local data we
// seed the server instead of wiping local.

const SYNC_DEBOUNCE_MS = 1200;
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let pushing = false;

function canNetwork(): boolean {
  return typeof fetch === "function";
}

function isEmpty(s: FishFinderState): boolean {
  return (
    s.lifelist.length === 0 &&
    s.visited_locations.length === 0 &&
    s.badges.length === 0
  );
}

function scheduleSync() {
  if (!canNetwork()) return;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    void pushToServer();
  }, SYNC_DEBOUNCE_MS);
}

// Remember the last-successfully-sent body so we can skip a PUT when
// nothing actually changed since the last sync — see
// state-put-full-2mb-payload-every-mutation. Cleared on sign-out (via
// wipeLocal) so a new account's first mutation still syncs.
let lastSentBody: string | null = null;
let lastPushErrorToastedAt = 0;

async function pushToServer(): Promise<void> {
  if (!canNetwork() || pushing) return;
  pushing = true;
  const sent = state; // reference identity == "no mutation raced in"
  const body = JSON.stringify(sent);
  if (body === lastSentBody) {
    // Debounced-mutations landed on the same state we already stored;
    // no-op the write but drop the dirty flag so we don't keep re-firing.
    setDirty(false);
    pushing = false;
    return;
  }
  try {
    const res = await fetch("/api/state", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body,
    });
    if (res.status === 503) {
      logEvent("state.push_error", {
        status: 503,
        reason: "not_configured",
      });
      return; // durable storage not configured yet
    }
    if (!res.ok) {
      let reason = "";
      try {
        const parsed = (await res.clone().json()) as { reason?: string };
        reason = parsed?.reason ?? "";
      } catch {
        /* ignore */
      }
      // Surface the failure into diagnostics so a Free-tier 402 or a
      // 413 payload-too-large stops looking like "the app is fine".
      logEvent("state.push_error", {
        status: res.status,
        reason,
        size: body.length,
      });
      // Toast the user at most once every 30 s on hard-fail statuses so
      // they know their edits aren't syncing. Retry keeps running.
      if (
        (res.status === 402 || res.status === 413) &&
        Date.now() - lastPushErrorToastedAt > 30_000
      ) {
        lastPushErrorToastedAt = Date.now();
        toast(
          res.status === 402
            ? "Sync paused — your plan doesn't cover the current state size."
            : "Your collection is too large to sync — trim it and try again.",
        );
      }
      return; // keep dirty; retry on next mutation/online/load
    }
    const stored = (await res.json()) as FishFinderState;
    if (state === sent) {
      setDirty(false);
      lastSentBody = body;
      if (JSON.stringify(stored) !== JSON.stringify(state)) {
        commit(stored, false);
        // The server rewrote our state (e.g. sanitize dropped fields).
        // Update the memo so the next sync doesn't skip against a stale body.
        lastSentBody = JSON.stringify(stored);
      }
    }
    // else: a mutation happened mid-flight; it already scheduled another sync
  } catch (err) {
    logEvent("state.push_error", {
      status: 0,
      error: err instanceof Error ? err.message : String(err),
    });
    /* offline — stay dirty, retry later */
  } finally {
    pushing = false;
  }
}

let hydrated = false;

// Call once on app start. Pulls the durable copy (or seeds it from local).
export async function hydrate(): Promise<void> {
  if (hydrated || !canNetwork()) return;
  hydrated = true;

  if (dirty && !isEmpty(state)) {
    // This device has unsynced local edits — push them (LWW, local wins).
    logEvent("state.hydrate", { direction: "seed-server-from-local-dirty" });
    await pushToServer();
    return;
  }
  if (dirty && isEmpty(state)) {
    // Dirty flag set but the local copy is EMPTY. This is the wipe trap:
    // the browser evicted the saved state (common on iOS Safari) but kept
    // the "needs sync" flag, so a blind push would overwrite the server
    // copy with nothing. Distrust the empty local — drop the flag and fall
    // through to the pull below so we adopt the authoritative server copy
    // instead of destroying it. See state-empty-dirty-push-wipes-server.
    logEvent("state.hydrate", { direction: "distrust-empty-dirty-pull" });
    setDirty(false);
  }
  const localCounts = {
    lifelist_count_before: state.lifelist.length,
    trips_count_before: state.trips.length,
    visits_count_before: state.visited_locations.length,
  };
  try {
    const res = await fetch("/api/state");
    if (!res.ok) {
      logEvent("state.pull_error", {
        status: res.status,
        reason: res.status === 503 ? "not_configured" : "http_error",
      });
      return;
    }
    const server = (await res.json()) as FishFinderState;
    if (isEmpty(server) && !isEmpty(state)) {
      logEvent("state.hydrate", {
        direction: "seed-server-from-local",
        ...localCounts,
      });
      await pushToServer(); // first migration: seed durable store from local
    } else if (
      !isEmpty(server) &&
      JSON.stringify(server) !== JSON.stringify(state)
    ) {
      logEvent("state.hydrate", {
        direction: "adopt-server",
        ...localCounts,
        lifelist_count_after: server.lifelist.length,
        trips_count_after: server.trips.length,
        visits_count_after: server.visited_locations.length,
      });
      commit(server, false); // adopt authoritative server copy
    } else {
      logEvent("state.pull_ok", { direction: "no_change", ...localCounts });
    }
  } catch (err) {
    logEvent("state.pull_error", {
      status: 0,
      reason: "network",
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

// ---- account switching (multi-user) ----
//
// localStorage is shared by every account on a browser, so on sign-in we
// must not let one account's cached/dirty data leak into another's. We track
// the last signed-in account; a different one wipes the local cache before
// pulling that account's authoritative server copy. Same account on reload
// keeps its offline cache.

const ACCOUNT_KEY = "fish-finder:account";

function wipeLocal(): void {
  state = { ...EMPTY };
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  setDirty(false);
  hydrated = false;
  listeners.forEach((l) => l());
}

// Called when an account becomes active. Clears another account's residue,
// then hydrates this account from the server.
export async function prepareAccount(email: string): Promise<void> {
  let prev: string | null = null;
  try {
    prev = localStorage.getItem(ACCOUNT_KEY);
  } catch {
    /* ignore */
  }
  if (prev !== email) {
    wipeLocal();
    try {
      localStorage.setItem(ACCOUNT_KEY, email);
    } catch {
      /* ignore */
    }
  }
  hydrated = false; // re-pull on every (re)auth, e.g. logout→login
  await hydrate();
}

// Re-pull the durable copy, discarding the not-dirty local cache. Used after
// a server-side change the client didn't make (e.g. legacy-data claim).
export async function refreshFromServer(): Promise<void> {
  setDirty(false);
  hydrated = false;
  await hydrate();
}

// Called after a successful email change. The local cache is keyed by email
// (ACCOUNT_KEY); without this, the next (re)auth would see a "different"
// account and wipe the cache. Server data is keyed by user_id, so only this
// pointer needs to move — no data is touched.
export function updateAccountEmail(email: string): void {
  try {
    localStorage.setItem(ACCOUNT_KEY, email);
  } catch {
    /* ignore */
  }
}

// Called on sign-out: drop this account's local copy so nothing lingers in
// the browser for the next person.
export function forgetAccount(): void {
  wipeLocal();
  try {
    localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    /* ignore */
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (dirty) void pushToServer();
  });
}
